import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  frequency: string;
  features: string[];
  savings: string;
  popular?: boolean;
}

interface SubscriptionBannerProps {
  plans: SubscriptionPlan[];
}

const SubscriptionBanner = ({ plans }: SubscriptionBannerProps) => {
  return (
    <section className="py-12 px-6 bg-gradient-to-br from-secondary via-secondary to-primary">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl font-bold text-white mb-3">
            Subscribe & Save More
          </h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Get fresh fish delivered regularly with our subscription plans and enjoy exclusive benefits
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-card rounded-lg p-8 shadow-elevation-lg transition-smooth hover:shadow-elevation-xl hover-lift ${
                plan.popular ? 'ring-4 ring-accent' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-2 bg-accent text-accent-foreground rounded-full font-bold text-sm shadow-elevation-md">
                    <Icon name="StarIcon" size={16} variant="solid" />
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="font-data text-4xl font-bold text-primary">
                    ₹{plan.price}
                  </span>
                  <span className="text-muted-foreground">/{plan.frequency}</span>
                </div>
                <span className="inline-block px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium">
                  Save {plan.savings}
                </span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <span className="text-foreground text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                href="/checkout-payment"
                className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-md font-medium transition-smooth hover:shadow-elevation-md hover-lift ${
                  plan.popular
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-border'
                }`}
              >
                <span>Subscribe Now</span>
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-white/80 mb-4">
            All plans include free delivery, flexible scheduling, and easy cancellation
          </p>
          <Link
            href="/product-listing"
            className="inline-flex items-center gap-2 text-white font-medium hover:text-white/80 transition-smooth"
          >
            <span>Learn more about subscriptions</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionBanner;